import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const CALENDLY_URL = "";

const INSTITUTION_TYPES = [
  "Commercial Banks",
  "Rural Banks",
  "Savings & Loans",
  "Credit Unions",
  "Insurance Companies",
  "Investment Companies",
  "Pension Providers",
  "FinTech Companies",
  "Digital Lenders",
  "Mortgage Institutions",
  "Microfinance Institutions",
  "Asset Finance Companies",
  "Hire Purchase Companies",
  "Payment Service Providers",
  "Other",
];

const INITIAL = {
  name: "",
  company: "",
  email: "",
  phone: "",
  institution: "",
};

function Demo() {
  const reduce = useReducedMotion();
  const [form, setForm] = useState(INITIAL);
  const [sent, setSent] = useState(false);
  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (sent) setSent(false);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setSent(true);
  };

  return (
    <section
      id="contact"
      className="relative overflow-hidden bg-[#f3f7f5] text-navy"
      aria-labelledby="demo-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_0%_0%,rgba(229,184,11,0.12),transparent_55%),radial-gradient(55%_40%_at_100%_100%,rgba(12,31,27,0.08),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.36]"
          style={{
            backgroundImage: "radial-gradient(rgba(12,31,27,0.08) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <motion.header
          className="max-w-3xl"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
            Request a live demonstration
          </p>
          <h2
            id="demo-heading"
            className="mt-4 font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(2rem,5vw,3.75rem)]"
          >
            Experience SimFinance
            <span className="mt-1 block">firsthand</span>
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-navy/60 sm:text-lg">
            Schedule a personalized demonstration with our product specialists and discover how
            SimFinance can transform your organization.
          </p>
        </motion.header>

        <motion.div
          className="mt-12 grid overflow-hidden border border-navy/10 bg-navy/10 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <form className="bg-white px-6 py-8 sm:px-8 sm:py-10" onSubmit={onSubmit} noValidate>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Name" name="name" value={form.name} onChange={onChange} required />
              <Field label="Company" name="company" value={form.company} onChange={onChange} required />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                required
              />
              <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} />
              <div className="sm:col-span-2">
                <label htmlFor="institution" className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/40">
                  Institution type
                </label>
                <select
                  id="institution"
                  name="institution"
                  value={form.institution}
                  onChange={onChange}
                  required
                  className="mt-2 min-h-12 w-full cursor-pointer border border-navy/15 bg-[#f3f7f5] px-4 text-sm text-navy outline-none transition focus:border-gold focus:ring-2 focus:ring-gold/40"
                >
                  <option value="" disabled>
                    Select institution type
                  </option>
                  {INSTITUTION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="submit"
              className="mt-8 inline-flex min-h-12 cursor-pointer items-center gap-2 rounded-full bg-gold px-6 text-sm font-semibold text-navy transition hover:bg-[#f0c52a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2"
            >
              Book My Demo
              <ArrowIcon />
            </button>
            {sent ? (
              <p className="mt-4 text-sm text-navy/55" role="status">
                Details captured. Choose a time in the calendar when Calendly is connected.
              </p>
            ) : null}
          </form>

          <CalendlySlot />
        </motion.div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", required = false }) {
  return (
    <div>
      <label htmlFor={name} className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/40">
        {label}
        {required ? <span className="text-gold"> *</span> : null}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={name === "email" ? "email" : name === "name" ? "name" : name === "phone" ? "tel" : "organization"}
        className="mt-2 min-h-12 w-full border border-navy/15 bg-[#f3f7f5] px-4 text-sm text-navy outline-none transition placeholder:text-navy/30 focus:border-gold focus:ring-2 focus:ring-gold/40"
      />
    </div>
  );
}

function CalendlySlot() {
  if (CALENDLY_URL) {
    return (
      <div className="min-h-[28rem] bg-white lg:min-h-full">
        <iframe
          title="Select a preferred demo date"
          src={CALENDLY_URL}
          className="h-full min-h-[28rem] w-full border-0 lg:min-h-[36rem]"
        />
      </div>
    );
  }

  return (
    <div className="relative flex min-h-[28rem] flex-col bg-navy px-6 py-8 text-white sm:px-8 sm:py-10 lg:min-h-[36rem]">
      <div className="pointer-events-none absolute -right-16 top-8 h-48 w-48 rounded-full bg-royal/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 left-10 h-40 w-40 rounded-full bg-gold/12 blur-3xl" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-gold via-royal to-gold" />

      <div className="relative flex items-center justify-between gap-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Preferred date</p>
        <span className="font-mono text-[11px] tracking-[0.18em] text-white/35">Calendly</span>
      </div>

      <div className="relative mt-8 flex flex-1 flex-col items-center justify-center border border-dashed border-white/15 px-6 py-12 text-center">
        <CalendarIcon />
        <p className="mt-5 text-lg font-semibold tracking-tight">Calendar embed</p>
        <p className="mt-2 max-w-sm text-sm leading-relaxed text-white/55">
          The Calendly scheduler will sit here. Paste the inline embed URL into this section when
          it is ready.
        </p>
      </div>
    </div>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 40 40" fill="none" aria-hidden="true" className="h-12 w-12 text-gold">
      <rect x="5" y="8" width="30" height="26" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M5 16H35" stroke="currentColor" strokeWidth="1.6" />
      <path d="M13 5V11M27 5V11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <rect x="11" y="21" width="4" height="4" fill="currentColor" opacity="0.9" />
      <rect x="18" y="21" width="4" height="4" fill="currentColor" opacity="0.45" />
      <rect x="25" y="21" width="4" height="4" fill="currentColor" opacity="0.45" />
      <rect x="11" y="27" width="4" height="4" fill="currentColor" opacity="0.45" />
      <rect x="18" y="27" width="4" height="4" fill="currentColor" opacity="0.9" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path
        d="M3 8H13M13 8L9 4M13 8L9 12"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default Demo;
